import React, { useState, useEffect } from "react";
import { Box, Typography, Paper, Button } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { ResponsivePie } from "@nivo/pie";
import StatBox from "../../components/StatBox";
import Header from "../../components/Header";

// Color palette for the pie charts
const pieColors = [
    "#FF5733", // Reddish
    "#33FF57", // Greenish
    "#3357FF", // Blueish
    "#F39C12", // Orange
    "#8E44AD", // Purple
];

const DashboardSeparationTracking = () => {
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pageSize, setPageSize] = useState(10);
    // States for filtering via pie slice clicks
    const [selectedWorkqueue, setSelectedWorkqueue] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState(null);

    // Fetch data from the API endpoint and deduplicate by ProcessID (keeping the latest CreatedOn)
    useEffect(() => {
        fetch("https://azuresql.swancreate.com/api/swa/lo_HR_Separation_tracking")
            .then((res) => res.json())
            .then((data) => {
                const grouped = data.reduce((acc, record) => {
                    if (
                        !acc[record.ProcessID] ||
                        new Date(record.CreatedOn) > new Date(acc[record.ProcessID].CreatedOn)
                    ) {
                        acc[record.ProcessID] = record;
                    }
                    return acc;
                }, {});
                const uniqueRecords = Object.values(grouped);
                setRecords(uniqueRecords);
                setLoading(false);
            })
            .catch((err) => {
                console.error("Error fetching separation tracking data:", err);
                setLoading(false);
            });
    }, []);

    // Total unique processes count
    const totalRecords = records.length;

    // Filter records based on the selected filters
    const filteredRecords = records.filter((rec) => {
        if (selectedWorkqueue && rec.Workqueue !== selectedWorkqueue) return false;
        if (selectedStatus && rec.Status !== selectedStatus) return false;
        return true;
    });

    // Group all records by Workqueue for the Workqueue Pie Chart
    const workqueueCounts = records.reduce((acc, rec) => {
        const wq = rec.Workqueue || "Unknown";
        acc[wq] = (acc[wq] || 0) + 1;
        return acc;
    }, {});
    const workqueuePieData = Object.entries(workqueueCounts).map(([key, value], index) => ({
        id: key,
        label: key,
        value,
        color: pieColors[index % pieColors.length],
    }));

    // Group all records by Status for the Status Pie Chart
    const statusCounts = records.reduce((acc, rec) => {
        const stat = rec.Status || "Unknown";
        acc[stat] = (acc[stat] || 0) + 1;
        return acc;
    }, {});
    const statusPieData = Object.entries(statusCounts).map(([key, value], index) => ({
        id: key,
        label: key,
        value,
        color: pieColors[index % pieColors.length],
    }));

    // Define columns for the DataGrid
    const columns = [
        { field: "ProcessID", headerName: "Process ID", flex: 1 },
        { field: "EmployeeID", headerName: "Employee ID", flex: 1 },
        { field: "FirstName", headerName: "First Name", flex: 1 },
        { field: "LastName", headerName: "Last Name", flex: 1 },
        { field: "Workqueue", headerName: "Workqueue", flex: 1 },
        { field: "Status", headerName: "Status", flex: 1 },
    ];

    // Map filtered records to DataGrid rows
    const rows = filteredRecords.map((rec, index) => ({
        id: index, // or use rec.ProcessID if unique
        ...rec,
    }));

    // Reset filters handler
    const resetFilters = () => {
        setSelectedWorkqueue(null);
        setSelectedStatus(null);
    };

    // CSV Download: exports current filtered rows as CSV
    const downloadCSV = () => {
        const csvHeaders = columns.map((col) => col.headerName).join(",");
        const csvRows = filteredRecords.map((row) =>
            columns
                .map((col) => {
                    let cell = row[col.field] || "";
                    if (typeof cell === "string") {
                        cell = cell.replace(/"/g, '""');
                        return `"${cell}"`;
                    }
                    return cell;
                })
                .join(",")
        );
        const csvContent = [csvHeaders, ...csvRows].join("\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "separation_tracking.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <Box m="20px">
            <Header title="DASHBOARD SEPARATION TRACKING" subtitle="Track Involuntary Separation Requests" />

            {/* Stat Box */}
            <Box display="grid" gridTemplateColumns="repeat(12, 1fr)" gap="20px" my="20px">
                <Box gridColumn="span 12">
                    <StatBox title={totalRecords} subtitle="Total Separation Records" />
                </Box>
            </Box>

            {/* Pie Charts */}
            <Box display="flex" gap="20px" mb="20px">
                {/* Workqueue Distribution Pie Chart */}
                <Paper sx={{ flex: 1, height: 450, p: 2, backgroundColor: "#2c3e50" }}>
                    <Typography variant="h6" color="#fff" mb={2}>
                        Workqueue Distribution
                    </Typography>
                    <Box height="400px">
                        <ResponsivePie
                            data={workqueuePieData}
                            margin={{ top: 40, right: 120, bottom: 80, left: 80 }}
                            innerRadius={0}
                            padAngle={0.7}
                            cornerRadius={5}
                            colors={workqueuePieData.map((d) => d.color)}
                            borderWidth={1}
                            borderColor={{ from: "color", modifiers: [["darker", 0.3]] }}
                            radialLabelsSkipAngle={10}
                            radialLabelsTextColor="#fff"
                            radialLabelsLinkColor={{ from: "color" }}
                            sliceLabelsSkipAngle={10}
                            sliceLabelsTextColor="#fff"
                            legends={[
                                {
                                    anchor: "right",
                                    direction: "column",
                                    translateX: 20,
                                    translateY: 0,
                                    itemWidth: 100,
                                    itemHeight: 18,
                                    itemTextColor: "#fff",
                                    symbolSize: 18,
                                    symbolShape: "circle",
                                    effects: [
                                        {
                                            on: "hover",
                                            style: { itemTextColor: "#fff" },
                                        },
                                    ],
                                },
                            ]}
                            theme={{
                                legends: { text: { fontSize: 16 } },
                                labels: { text: { fontSize: 16, fill: "#fff" } },
                            }}
                            onClick={(datum) => {
                                // When a slice is clicked, set the Workqueue filter and reset Status filter.
                                if (selectedWorkqueue === datum.id) {
                                    setSelectedWorkqueue(null);
                                } else {
                                    setSelectedWorkqueue(datum.id);
                                }
                                setSelectedStatus(null);
                            }}
                        />
                    </Box>
                </Paper>

                {/* Status Distribution Pie Chart */}
                <Paper sx={{ flex: 1, height: 450, p: 2, backgroundColor: "#2c3e50" }}>
                    <Typography variant="h6" color="#fff" mb={2}>
                        Status Distribution
                    </Typography>
                    <Box height="400px">
                        <ResponsivePie
                            data={statusPieData}
                            margin={{ top: 40, right: 120, bottom: 80, left: 80 }}
                            innerRadius={0}
                            padAngle={0.7}
                            cornerRadius={5}
                            colors={statusPieData.map((d) => d.color)}
                            borderWidth={1}
                            borderColor={{ from: "color", modifiers: [["darker", 0.3]] }}
                            radialLabelsSkipAngle={10}
                            radialLabelsTextColor="#fff"
                            radialLabelsLinkColor={{ from: "color" }}
                            sliceLabelsSkipAngle={10}
                            sliceLabelsTextColor="#fff"
                            legends={[
                                {
                                    anchor: "right",
                                    direction: "column",
                                    translateX: 20,
                                    translateY: 0,
                                    itemWidth: 100,
                                    itemHeight: 18,
                                    itemTextColor: "#fff",
                                    symbolSize: 18,
                                    symbolShape: "circle",
                                    effects: [
                                        {
                                            on: "hover",
                                            style: { itemTextColor: "#fff" },
                                        },
                                    ],
                                },
                            ]}
                            theme={{
                                legends: { text: { fontSize: 16 } },
                                labels: { text: { fontSize: 16, fill: "#fff" } },
                            }}
                            onClick={(datum) => {
                                // When a slice is clicked, set the Status filter and reset Workqueue filter.
                                if (selectedStatus === datum.id) {
                                    setSelectedStatus(null);
                                } else {
                                    setSelectedStatus(datum.id);
                                }
                                setSelectedWorkqueue(null);
                            }}
                        />
                    </Box>
                </Paper>
            </Box>

            {/* Control Buttons */}
            <Box display="flex" gap={2} mb="20px">
                <Button variant="contained" color="primary" onClick={resetFilters}>
                    Reset Filters
                </Button>
                <Button variant="contained" color="success" onClick={downloadCSV}>
                    Download CSV
                </Button>
            </Box>

            {/* Data Table */}
            <Box
                m="20px 0"
                height="600px"
                sx={{
                    "& .MuiDataGrid-root": { border: "none" },
                    "& .MuiDataGrid-row:nth-of-type(odd)": { backgroundColor: "#34495e" },
                    "& .MuiDataGrid-row:nth-of-type(even)": { backgroundColor: "#2c3e50" },
                    "& .MuiDataGrid-columnHeaders": { backgroundColor: "#2c3e50", borderBottom: "none" },
                    "& .MuiCheckbox-root.Mui-checked": { color: "#fff" },
                }}
            >
                <DataGrid
                    rows={rows}
                    columns={columns}
                    loading={loading}
                    pageSize={pageSize}
                    onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                    rowsPerPageOptions={[5, 10, 25]}
                    density="compact"
                    checkboxSelection
                    disableSelectionOnClick
                />
            </Box>
        </Box>
    );
};

export default DashboardSeparationTracking;
